import React, { useState } from 'react'
import {
  Typography,
  IconButton,
  Box,
  List,
  Autocomplete,
  ListItemButton,
  autocompleteClasses,
  ListItem,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { styled } from '@mui/material/styles'
import { config } from 'src/config'
import { Organization } from '@/interfaces/organization'
import { useOrganization } from '@/hooks'
import { Modal, TextField, SuccessMessage } from '@/components/ui'

interface Props {
  onSelectOrganization: (organization: Organization) => void
}

const OrganizationStateInput = styled(Autocomplete)(({ theme }) => ({
  height: 42,
  marginBottom: theme.spacing(6),

  [`.${autocompleteClasses.inputRoot}`]: {
    padding: 0,
  },

  [`.${autocompleteClasses.option}`]: {
    paddingTop: 0,
    paddingBottom: 0,
  },

  [`.${autocompleteClasses.paper}`]: {
    boxShadow: '0px 10px 15px rgba(152, 143, 168, 0.25)',
    borderRadius: '6px',
  },
})) as typeof Autocomplete

const OrganizationList = styled(List)`
  margin-top: 4px;
  background-color: ${({ theme }) => theme.palette.common.white};
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  box-shadow: 0px 10px 15px rgba(152, 143, 168, 0.25);
  border-radius: 6px;
`

const OrganizationListItem = styled(ListItemButton)`
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 2px solid ${({ theme }) => theme.custom.colors.common.grey};
  flex-direction: column;
  align-items: flex-start;
  &:last-child {
    border-bottom: none;
  }
`

const StateOption = styled(ListItem)`
  padding: ${({ theme }) => theme.spacing(2)}!important;
  border-bottom: 2px solid ${({ theme }) => theme.custom.colors.common.grey};
  width: 100%;
`

const OrganizationAddress = styled(Typography)`
  margin-top: 3px;
  color: ${({ theme }) => theme.custom.colors.common.grey4};
`

const EmailLink = styled('a')`
  text-decoration: underline;
  color: ${({ theme }) => theme.palette.common.white};
`

const SearchOrganization: React.FC<Props> = ({ onSelectOrganization }) => {
  const [isVisible, setVisibility] = useState<boolean>(false)
  const {
    selectedState,
    organizationName,
    organizationList,
    states,
    setOrganizationName,
    onChangeOrganizationName,
    onChangeState,
    buildOrganizationAddress,
  } = useOrganization()

  const onClickOrganization = (organization: Organization) => {
    setOrganizationName(organization.name)
    onSelectOrganization(organization)
    closePopup()
  }

  const openPopup = () => setVisibility(true)
  const closePopup = () => setVisibility(false)

  const renderOrganizationList = () => {
    if (organizationList.length === 0) return null
    return (
      <OrganizationList>
        {organizationList.map((organization) => (
          <OrganizationListItem key={organization.id} onClick={() => onClickOrganization(organization)}>
            <Typography variant="body1">{organization.name}</Typography>
            <OrganizationAddress variant="body2">
              {buildOrganizationAddress(organization, ['city', 'state', 'midasId'])}
            </OrganizationAddress>
          </OrganizationListItem>
        ))}
      </OrganizationList>
    )
  }

  return (
    <div>
      <IconButton size="small" edge="end" onClick={openPopup}>
        <SearchIcon />
      </IconButton>
      <Modal
        title="Look Up Org ID"
        isOpen={isVisible}
        message={
          organizationName ? (
            <SuccessMessage>
              Don't see your organization here? Check with your fundraising leader or{' '}
              <EmailLink href={`mailto:${config.customerSupport.email}`}>contact us.</EmailLink>
            </SuccessMessage>
          ) : undefined
        }
        onClose={closePopup}
      >
        <Box>
          <OrganizationStateInput
            id="search-state"
            options={states}
            getOptionLabel={(option) => option.name}
            value={selectedState}
            renderOption={(props, option) => {
              // eslint-disable-next-line react/prop-types
              const { key, ...optionProps } = props

              return (
                <StateOption key={key} {...optionProps}>
                  {option.name}
                </StateOption>
              )
            }}
            renderInput={({ inputProps, InputLabelProps, InputProps, ...rest }) => (
              <TextField
                {...rest}
                fullWidth
                slotProps={{ htmlInput: inputProps, inputLabel: InputLabelProps, input: InputProps }}
                label="Organization's state"
                variant="outlined"
              />
            )}
            onChange={onChangeState}
          />
          <TextField
            fullWidth
            id="organization-name"
            label="Organization's name"
            value={organizationName}
            variant="outlined"
            onChange={onChangeOrganizationName}
          />
          {renderOrganizationList()}
        </Box>
      </Modal>
    </div>
  )
}

export default SearchOrganization
